import {
  BuiltinModuleTag,
  ComparisonOperator,
  SystemTemplateSlug,
  TemplateFieldId,
  templatesOptions,
} from "@scayla/api-client-fetch";
import { useQuery } from "@tanstack/vue-query";
import type { MaybeRefOrGetter } from "vue";
import { computed, toValue } from "vue";
import { useRouter } from "vue-router";

import { Routes } from "@/router";
import { useWorkspace } from "@/store/workspace";
import { showToastForErrorRef } from "@/utils/toast";

/**
 * Resolves whether an Email-draft compose form can be opened for the current tenant (Emails
 * module present, its draft template found, module-level create permission active — deliberately
 * not template-level, matching #3451's product decision), and how to navigate an email address
 * into one. Callers must fall back to `mailto:` themselves when `canComposeEmailDraft` is false.
 */
export function useEmailComposeDraft(options?: { enabled?: MaybeRefOrGetter<boolean> }) {
  const router = useRouter();
  const workspace = useWorkspace();

  const emailsModuleId = computed(
    () => workspace.modules?.find((m) => m.builtinModule === BuiltinModuleTag.EMAILS)?.id,
  );

  const { data: emailDraftTemplates, error: emailDraftTemplatesError } = useQuery(
    computed(() => ({
      ...templatesOptions({
        body: {
          tenantId: workspace.tenantId,
          organizationId: workspace.orgId ?? undefined,
          moduleId: emailsModuleId.value,
          filter: {
            kind: "condition",
            field: TemplateFieldId.TEMPLATE_SYSTEM_TEMPLATE_SLUG,
            operator: ComparisonOperator.EQ,
            value: SystemTemplateSlug.SYSTEM_TEMPLATE_SLUG_EMAIL_DRAFT,
          },
        },
      }),
      enabled: toValue(options?.enabled ?? true) && !!workspace.tenantId && !!emailsModuleId.value,
    })),
  );
  showToastForErrorRef(emailDraftTemplatesError);
  const emailDraftTemplateId = computed(() => emailDraftTemplates.value?.items[0]?.id);

  const canComposeEmailDraft = computed(
    () =>
      !!emailsModuleId.value &&
      !!emailDraftTemplateId.value &&
      workspace.getModulePermissions(emailsModuleId.value).active,
  );

  const emailDraftRoute = (address: string) => ({
    name: Routes.RECORD_CREATE,
    params: { moduleId: emailsModuleId.value, templateId: emailDraftTemplateId.value },
    query: { toEmail: address },
  });

  const getEmailHref = (address: string) =>
    canComposeEmailDraft.value ? router.resolve(emailDraftRoute(address)).href : `mailto:${address}`;

  const navigateToEmailDraft = (address: string) => router.push(emailDraftRoute(address));

  return {
    emailsModuleId,
    emailDraftTemplateId,
    canComposeEmailDraft,
    emailDraftRoute,
    getEmailHref,
    navigateToEmailDraft,
  };
}
